// Feature-use counting (guitarparty_analytics on the server).
//
// track('metronome_started') queues an event; the queue goes to the server
// in one request every few seconds, and as the page is hidden or closed.
// The server only keeps the events and properties it knows about (see
// apps/guitarparty_analytics/catalog.py), and stands each signed-in person
// in for a code that changes every week, so nothing here identifies anyone.
// Nothing is stored in the browser.

import { getCookie } from '../utils';

const ENDPOINT = '/api/v3/core/feature-use/';
const FLUSH_AFTER_MS = 5000;
const MAX_BATCH = 20;

let queue = [];
let timer = null;
const once = new Set();

export function flush({ keepalive = false } = {}) {
    clearTimeout(timer);
    timer = null;
    if (!queue.length) return;
    const events = queue.splice(0, MAX_BATCH);
    fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive,
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('guitarpartycsrftoken'),
        },
        body: JSON.stringify({ events }),
    }).catch(() => {});
    if (queue.length) flush({ keepalive });
}

export function track(name, props) {
    queue.push(props ? { name, props } : { name });
    if (queue.length >= MAX_BATCH) flush();
    else if (!timer) timer = setTimeout(flush, FLUSH_AFTER_MS);
}

// For things done over and over in one sitting (strumming, shortcuts,
// display settings): counts the first time per page view, so the numbers
// say how many visits used the feature rather than how many clicks.
export function trackOnce(name, props, key = '') {
    const id = `${name}:${key}`;
    if (once.has(id)) return;
    once.add(id);
    track(name, props);
}

if (typeof window !== 'undefined') {
    const flushNow = () => flush({ keepalive: true });
    window.addEventListener('pagehide', flushNow);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushNow();
    });
}
